import React, { useEffect, useMemo, useState } from "react";
import {
  downloadTestMarkReport,
  downloadAttendanceReport,
  downloadStudentsReport,
  downloadTeachersReport,
  getAttendanceReport,
  getClasses,
  getSubjects,
  getStudents,
  getTestMarkReport,
  getTestMarkSubjects
} from "../services/api";
import DateInput from "../components/DateInput";
import { formatDate } from "../utils/helpers";
import "./ReportsPage.css";

const currentMonth = () => new Date().toISOString().slice(0, 7);

const normalizeCourse = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return "";
  }

  return trimmed === "Fees" ? "General" : trimmed;
};

function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function ReportsPage() {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedClass, setSelectedClass] = useState("all");
  const [selectedSubject, setSelectedSubject] = useState("all");
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [selectedAttendanceClass, setSelectedAttendanceClass] = useState("all");
  const [selectedAttendanceBoard, setSelectedAttendanceBoard] = useState("all");
  const [selectedAttendanceCourse, setSelectedAttendanceCourse] = useState("all");
  const [selectedTestStudentId, setSelectedTestStudentId] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(currentMonth());
  const [selectedTestMonth, setSelectedTestMonth] = useState(currentMonth());
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [testFromDate, setTestFromDate] = useState("");
  const [testToDate, setTestToDate] = useState("");
  const [selectedTestSubject, setSelectedTestSubject] = useState("all");
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [testMarkRecords, setTestMarkRecords] = useState([]);
  const [testPerformance, setTestPerformance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);

  useEffect(() => {
    const loadStudents = async () => {
      try {
        const [studentsRes, classesRes, subjectsRes] = await Promise.all([
          getStudents("all", { fields: "lookup" }),
          getClasses(),
          getSubjects()
        ]);
        setStudents(studentsRes.data);
        setClasses(classesRes.data);
        setSubjects(subjectsRes.data);
        const testSubjectsRes = await getTestMarkSubjects();
        setSubjects((current) => Array.from(new Set([...current, ...testSubjectsRes.data])).sort());
      } catch (err) {
        console.error("Failed to load students for report:", err);
      }
    };

    loadStudents();
  }, []);

  const attendanceBoardOptions = useMemo(() => Array.from(
    new Set(students.map((student) => (student.board || "").trim()).filter(Boolean))
  ).sort(), [students]);

  const courseOptions = ["Science", "Commerce", "General"];

  const filteredAttendanceStudents = useMemo(() => students.filter((student) => {
    const matchesClass = selectedAttendanceClass === "all" || student.class === selectedAttendanceClass;
    const matchesBoard = selectedAttendanceBoard === "all" || (student.board || "") === selectedAttendanceBoard;
    const matchesCourse = selectedAttendanceCourse === "all" || normalizeCourse(student.course) === selectedAttendanceCourse;
    return matchesClass && matchesBoard && matchesCourse;
  }), [students, selectedAttendanceClass, selectedAttendanceBoard, selectedAttendanceCourse]);

  useEffect(() => {
    if (selectedStudentId && !filteredAttendanceStudents.some((student) => student._id === selectedStudentId)) {
      setSelectedStudentId("");
    }
  }, [filteredAttendanceStudents, selectedStudentId]);

  const handleDownloadStudents = async () => {
    const res = await downloadStudentsReport({ class: selectedClass });
    saveBlob(res.data, `students-report-${selectedClass}.xls`);
  };

  const handleDownloadTeachers = async () => {
    const res = await downloadTeachersReport({ subject: selectedSubject });
    saveBlob(res.data, `teachers-report-${selectedSubject}.xls`);
  };

  const buildFilters = () => {
    const filters = { studentId: selectedStudentId };
    if (selectedAttendanceClass !== "all") {
      filters.class = selectedAttendanceClass;
    }
    if (selectedAttendanceBoard !== "all") {
      filters.board = selectedAttendanceBoard;
    }
    if (selectedAttendanceCourse !== "all") {
      filters.course = selectedAttendanceCourse;
    }
    if (selectedMonth) {
      filters.month = selectedMonth;
    }
    if (fromDate || toDate) {
      delete filters.month;
      if (fromDate) {
        filters.fromDate = fromDate;
      }
      if (toDate) {
        filters.toDate = toDate;
      }
    }
    return filters;
  };

  const handleFetchAttendance = async () => {
    try {
      setLoading(true);
      const res = await getAttendanceReport(buildFilters());
      setAttendanceRecords(res.data);
    } catch (err) {
      console.error("Failed to load attendance report:", err);
      alert("Failed to load attendance report");
    } finally {
      setLoading(false);
    }
  };

  const buildTestFilters = () => {
    const filters = { studentId: selectedTestStudentId };
    if (selectedTestSubject && selectedTestSubject !== "all") {
      filters.subject = selectedTestSubject;
    }
    if (selectedTestMonth) {
      filters.month = selectedTestMonth;
    }
    if (testFromDate || testToDate) {
      delete filters.month;
      if (testFromDate) {
        filters.fromDate = testFromDate;
      }
      if (testToDate) {
        filters.toDate = testToDate;
      }
    }
    return filters;
  };

  const handleFetchTestMarks = async () => {
    if (!selectedTestStudentId) {
      alert("Please select a student first.");
      return;
    }

    try {
      setTestLoading(true);
      const res = await getTestMarkReport(buildTestFilters());
      setTestMarkRecords(res.data.records);
      setTestPerformance(res.data.performance);
    } catch (err) {
      console.error("Failed to load test mark report:", err);
      alert("Failed to load test mark report");
    } finally {
      setTestLoading(false);
    }
  };

  const handleDownloadTestMarks = async () => {
    if (!selectedTestStudentId) {
      alert("Please select a student first.");
      return;
    }

    const student = students.find((item) => item._id === selectedTestStudentId);
    const res = await downloadTestMarkReport(buildTestFilters());
    saveBlob(res.data, `${student?.name || "student"}-test-marks-report.xls`);
  };

  const handleDownloadAttendance = async () => {
    const student = students.find((item) => item._id === selectedStudentId);
    const res = await downloadAttendanceReport(buildFilters());
    const scope = student?.name
      || (selectedAttendanceClass !== "all" && `class-${selectedAttendanceClass}`)
      || (selectedAttendanceBoard !== "all" && selectedAttendanceBoard)
      || (selectedAttendanceCourse !== "all" && selectedAttendanceCourse)
      || "attendance";
    saveBlob(res.data, `${scope}-attendance-report.xls`);
  };

  return (
    <div className="reports-page">
      <div className="reports-page__section">
        <div className="reports-page__section-head">
          <div>
            <h1>Reports</h1>
            {/* <p>Download Excel reports for students, teachers, and student-wise attendance history.</p> */}
          </div>
        </div>

        <div className="reports-page__export-grid">
          <div className="reports-page__card">
            <span className="reports-page__label">Student Report</span>
            <h2>Students by Class</h2>
            <p>Download the student master list in Excel for all classes or one selected class.</p>
            <div className="reports-page__inline-filter">
              <label>Class</label>
              <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
                <option value="all">All Classes</option>
                {classes.map((cls) => (
                  <option key={cls} value={cls}>{cls}</option>
                ))}
              </select>
            </div>
            <button className="reports-page__primary-btn" onClick={handleDownloadStudents}>
              Download Students Excel
            </button>
          </div>

          <div className="reports-page__card">
            <span className="reports-page__label">Teacher Report</span>
            <h2>Teachers by Subject</h2>
            <p>Download the teacher master list in Excel for all subjects or one selected subject.</p>
            <div className="reports-page__inline-filter">
              <label>Subject</label>
              <select value={selectedSubject} onChange={(e) => setSelectedSubject(e.target.value)}>
                <option value="all">All Subjects</option>
                {subjects.map((subject) => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>
            <button className="reports-page__primary-btn" onClick={handleDownloadTeachers}>
              Download Teachers Excel
            </button>
          </div>
        </div>
      </div>

      <div className="reports-page__section">
        <div className="reports-page__section-head">
          <div>
            <h2>Attendance Report</h2>
            <p>Choose a student, class, board, or course, then filter by month or date range.</p>
          </div>
        </div>

        <div className="reports-page__filters">
          <div className="reports-page__filter-card">
            <label>Class</label>
            <select value={selectedAttendanceClass} onChange={(e) => setSelectedAttendanceClass(e.target.value)}>
              <option value="all">All Classes</option>
              {classes.map((cls) => (
                <option key={cls} value={cls}>{cls}</option>
              ))}
            </select>
          </div>

          <div className="reports-page__filter-card">
            <label>Board</label>
            <select value={selectedAttendanceBoard} onChange={(e) => setSelectedAttendanceBoard(e.target.value)}>
              <option value="all">All Boards</option>
              {attendanceBoardOptions.map((board) => (
                <option key={board} value={board}>{board}</option>
              ))}
            </select>
          </div>

          <div className="reports-page__filter-card">
            <label>Course</label>
            <select value={selectedAttendanceCourse} onChange={(e) => setSelectedAttendanceCourse(e.target.value)}>
              <option value="all">All Courses</option>
              {courseOptions.map((course) => (
                <option key={course} value={course}>{course}</option>
              ))}
            </select>
          </div>

          <div className="reports-page__filter-card">
            <label>Student</label>
            <select value={selectedStudentId} onChange={(e) => setSelectedStudentId(e.target.value)}>
              <option value="">All Students</option>
              {filteredAttendanceStudents.map((student) => (
                <option key={student._id} value={student._id}>
                  {student.name} ({student.class})
                </option>
              ))}
            </select>
          </div>

          <div className="reports-page__filter-card">
            <label>Month</label>
            <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} />
          </div>

          <div className="reports-page__filter-card">
            <label>From Date</label>
            <DateInput value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>

          <div className="reports-page__filter-card">
            <label>To Date</label>
            <DateInput value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
        </div>

        <div className="reports-page__actions">
          <button className="reports-page__secondary-btn" onClick={handleFetchAttendance}>
            View Attendance Report
          </button>
          <button className="reports-page__primary-btn" onClick={handleDownloadAttendance}>
            Download Attendance Excel
          </button>
        </div>

        <div className="reports-page__table-card">
          {loading && <p className="reports-page__empty">Loading attendance report...</p>}
          {!loading && attendanceRecords.length === 0 && (
            <p className="reports-page__empty">No attendance report loaded yet.</p>
          )}
          {!loading && attendanceRecords.length > 0 && (
            <div className="reports-page__table-wrap">
              <table className="reports-page__table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Student</th>
                    <th>Class</th>
                    <th>Roll Number</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceRecords.map((record) => (
                    <tr key={record._id}>
                      <td>{formatDate(record.date)}</td>
                      <td>{record.student_name}</td>
                      <td>{record.class}</td>
                      <td>{record.rollNumber}</td>
                      <td>
                        <span className={`reports-page__badge reports-page__badge--${record.attendance_status}`}>
                          {record.attendance_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="reports-page__section">
        <div className="reports-page__section-head">
          <div>
            <h2>Student Test Performance Report</h2>
            <p>View weekly test marks and subject-wise performance for an individual student.</p>
          </div>
        </div>

        <div className="reports-page__filters">
          <div className="reports-page__filter-card">
            <label>Student</label>
            <select value={selectedTestStudentId} onChange={(e) => setSelectedTestStudentId(e.target.value)}>
              <option value="">Select Student</option>
              {students.map((student) => (
                <option key={student._id} value={student._id}>
                  {student.name} ({student.class})
                </option>
              ))}
            </select>
          </div>

          <div className="reports-page__filter-card">
            <label>Subject</label>
            <select value={selectedTestSubject} onChange={(e) => setSelectedTestSubject(e.target.value)}>
              <option value="all">All Subjects</option>
              {subjects.map((subject) => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
          </div>

          <div className="reports-page__filter-card">
            <label>Month</label>
            <input type="month" value={selectedTestMonth} onChange={(e) => setSelectedTestMonth(e.target.value)} />
          </div>

          <div className="reports-page__filter-card">
            <label>From Date</label>
            <DateInput value={testFromDate} onChange={(e) => setTestFromDate(e.target.value)} />
          </div>

          <div className="reports-page__filter-card">
            <label>To Date</label>
            <DateInput value={testToDate} onChange={(e) => setTestToDate(e.target.value)} />
          </div>
        </div>

        <div className="reports-page__actions">
          <button className="reports-page__secondary-btn" onClick={handleFetchTestMarks}>
            View Test Performance
          </button>
          <button className="reports-page__primary-btn" onClick={handleDownloadTestMarks}>
            Download Test Report Excel
          </button>
        </div>

        <div className="reports-page__export-grid">
          <div className="reports-page__card">
            <span className="reports-page__label">Subject Performance</span>
            <h2>Subject Wise Summary</h2>
            {testLoading && <p className="reports-page__empty">Loading test performance...</p>}
            {!testLoading && testPerformance.length === 0 && (
              <p className="reports-page__empty">No test performance loaded yet.</p>
            )}
            {!testLoading && testPerformance.length > 0 && (
              <div className="reports-page__table-wrap">
                <table className="reports-page__table">
                  <thead>
                    <tr>
                      <th>Subject</th>
                      <th>Tests</th>
                      <th>Marks</th>
                      <th>Average %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testPerformance.map((item) => (
                      <tr key={item.subject}>
                        <td>{item.subject}</td>
                        <td>{item.tests}</td>
                        <td>{item.totalMarks}/{item.totalMaxMarks}</td>
                        <td>{item.averagePercent}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="reports-page__card">
            <span className="reports-page__label">Detailed Marks</span>
            <h2>Weekly Test Records</h2>
            {testLoading && <p className="reports-page__empty">Loading test records...</p>}
            {!testLoading && testMarkRecords.length === 0 && (
              <p className="reports-page__empty">No test records loaded yet.</p>
            )}
            {!testLoading && testMarkRecords.length > 0 && (
              <div className="reports-page__table-wrap">
                <table className="reports-page__table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Test</th>
                      <th>Subject</th>
                      <th>Marks</th>
                      <th>Percent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testMarkRecords.map((record) => (
                      <tr key={record._id}>
                        <td>{formatDate(record.testDate)}</td>
                        <td>{record.testName}</td>
                        <td>{record.subject}</td>
                        <td>{record.marks}/{record.maxMarks}</td>
                        <td>{((record.marks / record.maxMarks) * 100).toFixed(2)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReportsPage;
